import { z } from 'zod';
import type { RoutableCareCallType } from './care-call-types';

/** Closed reason vocabulary for member ops and related calls — extended by follow-up tickets. */
export const CARE_CALL_REASON = [
  'registration',
  'hdhp',
  'pnp_non_clinical',
  'pnp_aftercare',
  'pact',
  'mat_medical_consult',
  'mda',
  'care_coordination',
  'family_support',
] as const;
export type CareCallReason = (typeof CARE_CALL_REASON)[number];
export const careCallReasonSchema = z.enum(CARE_CALL_REASON);

/** Client-parsed reads: a reason this bundle does not know reads as absent, not as a parse failure. */
export const tolerantCareCallReasonSchema = z.preprocess((raw) => {
  if (raw === undefined || raw === null) return raw;
  const parsed = careCallReasonSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}, careCallReasonSchema.nullable().optional());

/**
 * Reasons that only Pelago 1.0 imports carry: stored and read, but never
 * accepted on a booking request until staff can book them.
 */
export const IMPORT_ONLY_CARE_CALL_REASONS = [
  'pact',
  'mat_medical_consult',
  'mda',
  'care_coordination',
  'family_support',
] as const satisfies readonly CareCallReason[];
export type ImportOnlyCareCallReason = (typeof IMPORT_ONLY_CARE_CALL_REASONS)[number];

const importOnlyCareCallReasonSet = new Set<string>(IMPORT_ONLY_CARE_CALL_REASONS);

export function isImportOnlyCareCallReason(
  reason: string | null | undefined,
): reason is ImportOnlyCareCallReason {
  return reason != null && importOnlyCareCallReasonSet.has(reason);
}

/** The two Progress & Purpose check-in variants, carried as the booking reason. */
export const PROGRESS_PURPOSE_REASONS = ['pnp_non_clinical', 'pnp_aftercare'] as const;
export type ProgressPurposeReason = (typeof PROGRESS_PURPOSE_REASONS)[number];
export const progressPurposeReasonSchema = z.enum(PROGRESS_PURPOSE_REASONS);

const PROGRESS_PURPOSE_REASON_LABELS: Record<ProgressPurposeReason, string> = {
  pnp_non_clinical: 'Non-clinical P&P',
  pnp_aftercare: 'Aftercare P&P',
};

const progressPurposeReasonSet = new Set<string>(PROGRESS_PURPOSE_REASONS);

export function isProgressPurposeReason(
  reason: string | null | undefined,
): reason is ProgressPurposeReason {
  return reason != null && progressPurposeReasonSet.has(reason);
}

/** Staff-facing label for a P&P variant — also the prefix on its Healthie appointment notes. */
export function progressPurposeReasonLabel(reason: ProgressPurposeReason): string {
  return PROGRESS_PURPOSE_REASON_LABELS[reason];
}

const REASONS_BY_CALL_TYPE: Partial<Record<RoutableCareCallType, readonly CareCallReason[]>> = {
  follow_up: ['pact'],
  provider_call: ['mat_medical_consult', 'mda'],
  member_support: ['registration', 'hdhp', 'care_coordination', 'family_support'],
  progress_purpose_checkin: PROGRESS_PURPOSE_REASONS,
};

/** Call types a booking cannot be made for without a reason. */
const REASON_REQUIRED: ReadonlySet<RoutableCareCallType> = new Set(['progress_purpose_checkin']);

/**
 * Whether a booking of `callType` may carry `reason` (`undefined` = no reason).
 * A reason belongs to one call type's vocabulary; a P&P booking must name its
 * variant.
 */
export function callReasonAllowedForCallType(
  callType: RoutableCareCallType,
  reason: CareCallReason | undefined,
): boolean {
  if (reason === undefined) return !REASON_REQUIRED.has(callType);
  return REASONS_BY_CALL_TYPE[callType]?.includes(reason) ?? false;
}
